// src/components/sidebar/sidebar.tsx
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { useBackHandler } from '../../../platform/use-back-handler';
import SettingsFullscreenModal from '../settings/settings';
import SimpleSearchBar from '../search-bar/search-bar';
import DrawerBottomArea from './bottom-area/bottom-area';
import FilesDrawer from '../files-drawer/files-drawer';
import Notes from './notes/notes';
import { UniversalSpinner } from '../../../components/universal-spinner/universal-spinner';
import { AppAvatar } from '../../../components/avatar/avatar';
import { useAuth } from '../../../contexts/auth/auth-context';
import { usePressable } from '../../../hooks/use-pressable';

import {
  Overlay,
  Side,
  HeaderBar,
  IconBtn,
  Body,
  MainContent,
  SearchBarRow,
  AnimatedSheet,
  NotesSection,
  NotesLabel,
  AddNoteButton,
  AddNoteText,
  GoToHome,
  NotesListContainer,
  NoteItem,
  NoteRow,
  NoteTitle,
  NoteDate,
  LoadMoreButton,
  SpinnerWrap,
  DesktopSidePanel,
  NavLink,
  SearchResultsContainer,
  SearchSectionLabel,
  SearchResultItem,
  SearchResultTitle,
  SearchResultSnippet,
  SearchLoadingText,
  SearchEmptyText,
} from './sidebar.styled';

import { formatUpdatedAt, useSidebar } from './sidebar.hooks';
import { ScrollArea } from '../../../components/scroll-area/scroll-area';

type Props = {
  open: boolean;
  onClose: () => void;
  onCreateJournal?: (journalId: string) => void;
  onOpenUrgent: () => void;
  /** When set, opens the settings modal directly to this tab */
  openSettingsTab?: string | null;
  /** Called after the settings modal triggered by openSettingsTab closes */
  onSettingsClosed?: () => void;
  onViewFileInConversation?: (messageId: string, conversationId: string, searchQuery?: string) => void;
  onFileDeleted?: (fileId: string) => void;
  /** Desktop mode: render inline instead of Dialog */
  isDesktop?: boolean;
  /** Toggle sidebar open/collapsed (desktop only) */
  onToggle?: () => void;
  /** Open conversation view (desktop "My reflections" nav) */
  onOpenConversation?: () => void;
};

export default function Sidebar({
  open,
  onClose,
  onOpenUrgent,
  openSettingsTab,
  onSettingsClosed,
  onViewFileInConversation,
  onFileDeleted,
  isDesktop = false,
  onToggle,
  onOpenConversation,
}: Props) {
  const { profile } = useAuth();
  const collapsedInitials =
    `${String(profile?.first_name ?? '').trim().charAt(0)}${String(profile?.last_name ?? '').trim().charAt(0)}`.trim().toUpperCase() ||
    (profile?.nickname?.[0]?.toUpperCase() ?? '?');

  const {
    showSettings,
    setShowSettings,
    searchActive,
    setSearchActive,
    showFiles,
    setShowFiles,
    displayName,
    avatarUrl,
    planName,
    bodyRef,
    barRef,
    sheetTop,
    onSettings,
    onCloseSettings,
    onDocs,
    onCloseDocs,
    onLogout,
    onBilling,
    onNotifications,
    settingsInitialTab,

    // notes
    notesMeta,
    isLoadingNotesMeta,
    selectedNoteId,
    openNewNote,
    openExistingNote,
    onNoteSaved,
    onNoteDeleted,

    // search
    searchQuery,
    searchNotes,
    searchConversations,
    searchLoading,
    handleSearchChange,
  } = useSidebar(open);

  // Open settings directly from outside (e.g. avatar click in header)
  useEffect(() => {
    if (openSettingsTab) {
      setShowSettings(true);
    }
  }, [openSettingsTab, setShowSettings]);

  // Local state for Notes drawer (slide-in screen)
  const [showNotes, setShowNotes] = useState(false);
  const [showAllNotes, setShowAllNotes] = useState(false);

  const PAGE_SIZE = 10;

  // Defensive dedup: prevent duplicate notes from rendering even if a race
  // condition causes the same note ID to appear twice in the list.
  const uniqueNotes = useMemo(() => {
    const seen = new Set<string>();
    return notesMeta.filter((n) => {
      if (seen.has(n.id)) return false;
      seen.add(n.id);
      return true;
    });
  }, [notesMeta]);

  const visibleNotes = showAllNotes ? uniqueNotes : uniqueNotes.slice(0, PAGE_SIZE);
  const hasMore = uniqueNotes.length > PAGE_SIZE;
  const navigate = useNavigate();

  // Android back button: close overlays in reverse z-index order
  const closeNotes = useCallback(() => setShowNotes(false), []);
  const closeFiles = useCallback(() => setShowFiles(false), [setShowFiles]);
  const closeSettings = useCallback(() => setShowSettings(false), [setShowSettings]);

  useBackHandler('notes', showNotes, closeNotes);
  useBackHandler('files', showFiles, closeFiles);
  useBackHandler('settings', showSettings, closeSettings);
  useBackHandler('sidebar', open && !showSettings && !showFiles && !showNotes, onClose);

  // Press feedback handlers for Android WebView
  const pressCloseBtn = usePressable();
  const pressGoHome = usePressable();
  const pressReflections = usePressable();
  const pressNoteItem = usePressable();
  const pressAddNote = usePressable();
  const pressLoadMore = usePressable();

  /* ── Shared sidebar inner content ── */
  const sidebarContent = (
    <>
      <HeaderBar>
        <img
          src="/main-logo-white.svg"
          alt="logo"
          width={124}
          height="auto"
          draggable={false}
          style={{ cursor: 'pointer' }}
          onClick={() => {
            if (!isDesktop) onClose();
            navigate('/');
          }}
        />
        {isDesktop ? (
          <IconBtn aria-label="Collapse sidebar" onClick={onToggle} {...pressCloseBtn}>
            <img src="/close.svg" alt="close" width={44} height={44} draggable={false} />
          </IconBtn>
        ) : (
          <Dialog.Close asChild>
            <IconBtn aria-label="Close menu" {...pressCloseBtn}>
              <img src="/close.svg" alt="close" width={44} height={44} draggable={false} />
            </IconBtn>
          </Dialog.Close>
        )}
      </HeaderBar>

      <Body ref={bodyRef}>
        <SearchBarRow ref={barRef}>
          <SimpleSearchBar onActiveChange={setSearchActive} onSearchChange={handleSearchChange} />
        </SearchBarRow>

        <MainContent aria-hidden={searchActive}>
          <GoToHome
            type="button"
            onClick={() => onClose()}
            {...pressGoHome}
          >
            Go to home
          </GoToHome>

          {/* Desktop nav links */}
          {isDesktop && (
            <>
              <NavLink
                type="button"
                onClick={() => {
                  onOpenConversation?.();
                }}
                {...pressReflections}
              >
                My reflections
              </NavLink>
              <NavLink type="button" disabled>
                The Workroom
              </NavLink>
            </>
          )}

          {/* My Notes section */}
          <NotesSection>
            <NotesLabel>My Notes</NotesLabel>

            {/* Loading spinner */}
            {isLoadingNotesMeta && (
              <SpinnerWrap>
                <UniversalSpinner />
              </SpinnerWrap>
            )}

            {/* Empty state */}
            {!isLoadingNotesMeta && notesMeta.length === 0 && (
              <NotesLabel style={{ color: '#7d8496', fontSize: 13 }}>
                No notes yet
              </NotesLabel>
            )}

            {/* Scrollable notes list — fills available space */}
            {!isLoadingNotesMeta && visibleNotes.length > 0 && (
              <ScrollArea>
                <NotesListContainer>
                  {visibleNotes.map((n) => (
                    <NoteItem
                      key={n.id}
                      type="button"
                      onClick={() => {
                        openExistingNote(n.id);
                        setShowNotes(true);
                      }}
                      {...pressNoteItem}
                    >
                      <NoteRow>
                        <NoteTitle>{n.title || 'Untitled'}</NoteTitle>
                        <NoteDate>{formatUpdatedAt(n.updated_at)}</NoteDate>
                      </NoteRow>
                    </NoteItem>
                  ))}
                </NotesListContainer>
              </ScrollArea>
            )}

            {/* Load more button */}
            {!isLoadingNotesMeta && !showAllNotes && hasMore && (
              <LoadMoreButton type="button" onClick={() => setShowAllNotes(true)} {...pressLoadMore}>
                Load more
              </LoadMoreButton>
            )}

            <AddNoteButton
              type="button"
              onClick={() => {
                openNewNote();
                setShowNotes(true);
              }}
              aria-label="Add new note"
              {...pressAddNote}
            >
              <AddNoteText>+ Add new note</AddNoteText>
            </AddNoteButton>
          </NotesSection>
        </MainContent>

        <AnimatedSheet active={searchActive} top={sheetTop} aria-hidden={!searchActive}>
          <SearchResultsContainer>
            {/* Prompt when no query yet */}
            {(!searchQuery.trim() || searchQuery.trim().length < 2) && (
              <SearchEmptyText>Search conversations or notes</SearchEmptyText>
            )}

            {/* Loading */}
            {searchQuery.trim().length >= 2 && searchLoading && searchNotes.length === 0 && searchConversations.length === 0 && (
              <SearchLoadingText>Searching...</SearchLoadingText>
            )}

            {/* No results */}
            {searchQuery.trim().length >= 2 && !searchLoading && searchNotes.length === 0 && searchConversations.length === 0 && (
              <SearchEmptyText>No results found</SearchEmptyText>
            )}

            {/* Notes results */}
            {searchNotes.length > 0 && (
              <>
                <SearchSectionLabel>Notes</SearchSectionLabel>
                {searchNotes.map((n) => (
                  <SearchResultItem
                    key={n.id}
                    type="button"
                    onClick={() => {
                      openExistingNote(n.id);
                      setShowNotes(true);
                      setSearchActive(false);
                    }}
                  >
                    <SearchResultTitle>{n.title || 'Untitled'}</SearchResultTitle>
                    {n.snippet && <SearchResultSnippet>{n.snippet}</SearchResultSnippet>}
                  </SearchResultItem>
                ))}
              </>
            )}

            {/* Conversation results */}
            {searchConversations.length > 0 && (
              <>
                <SearchSectionLabel>Conversations</SearchSectionLabel>
                {searchConversations.map((c) => (
                  <SearchResultItem
                    key={c.message_id}
                    type="button"
                    onClick={() => {
                      setSearchActive(false);
                      if (!isDesktop) onClose();
                      onViewFileInConversation?.(c.message_id, c.conversation_id, searchQuery);
                    }}
                  >
                    <SearchResultTitle>{c.snippet || 'Message'}</SearchResultTitle>
                  </SearchResultItem>
                ))}
              </>
            )}

            {/* Subtle loading indicator when results already showing */}
            {searchQuery.trim().length >= 2 && searchLoading && (searchNotes.length > 0 || searchConversations.length > 0) && (
              <SearchLoadingText>Searching...</SearchLoadingText>
            )}
          </SearchResultsContainer>
        </AnimatedSheet>

        <DrawerBottomArea
          displayName={displayName}
          avatarUrl={avatarUrl}
          planName={planName}
          onSettings={onSettings}
          onBilling={onBilling}
          onNotifications={onNotifications}
          onDocs={onDocs}
          onLogout={onLogout}
          onCloseDrawer={isDesktop ? (onToggle ?? onClose) : onClose}
          onOpenUrgent={onOpenUrgent}
        />
      </Body>
    </>
  );

  return (
    <>
      {/* ── Desktop: inline sidebar ── */}
      {isDesktop && (
        <DesktopSidePanel collapsed={!open}>
          {open ? (
            sidebarContent
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', height: '100%', padding: '16px 0', boxSizing: 'border-box' }}>
              <img
                src="/logo.svg"
                alt="Mailander"
                width={24}
                height={24}
                draggable={false}
                style={{ cursor: 'pointer' }}
                onClick={onToggle}
              />
              <AppAvatar
                onClick={onToggle ?? (() => {})}
                userId={String(profile?.id ?? '')}
                version={(profile as any)?.avatar_version ?? (profile as any)?.updated_at ?? null}
                hasAvatar={!!profile?.avatar_url}
                alt="You"
                initials={collapsedInitials}
                width={28}
                height={28}
                imgStyle={{ borderRadius: '999px', objectFit: 'cover', cursor: 'pointer' }}
                fallbackStyle={{ borderRadius: '999px', cursor: 'pointer' }}
              />
            </div>
          )}
        </DesktopSidePanel>
      )}

      {/* ── Mobile: Dialog drawer ── */}
      {!isDesktop && (
        <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
          <Dialog.Portal>
            {!showNotes && <Overlay forceMount />}
            <Side forceMount aria-describedby={undefined}>
              <Dialog.Title style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}>
                Menu
              </Dialog.Title>
              <Dialog.Title
                style={{
                  position: 'absolute',
                  width: 1,
                  height: 1,
                  padding: 0,
                  margin: -1,
                  overflow: 'hidden',
                  clip: 'rect(0,0,0,0)',
                  whiteSpace: 'nowrap',
                  border: 0,
                }}
              >
                Main Menu
              </Dialog.Title>
              {sidebarContent}
            </Side>
          </Dialog.Portal>
        </Dialog.Root>
      )}

      {/* Settings fullscreen modal */}
      <SettingsFullscreenModal
        open={showSettings}
        initialTab={(openSettingsTab as typeof settingsInitialTab) || settingsInitialTab}
        onClose={() => {
          setShowSettings(false);
          onCloseSettings();
          onSettingsClosed?.();
        }}
      />

      {/* Files drawer (left → right), opened from "My Files" */}
      <FilesDrawer
        open={showFiles}
        onClose={() => {
          setShowFiles(false);
          onCloseDocs();
        }}
        onViewFileInConversation={(messageId, conversationId) => {
          setShowFiles(false);
          onCloseDocs();
          if (!isDesktop) onClose();
          onViewFileInConversation?.(messageId, conversationId);
        }}
        {...(onFileDeleted !== undefined ? { onFileDeleted } : {})}
      />

      {/* Notes screen – slide-in left → right over the app */}
      <Notes
        open={showNotes}
        onClose={() => setShowNotes(false)}
        noteId={selectedNoteId}
        onSaved={onNoteSaved}
        onDeleted={onNoteDeleted}
      />
    </>
  );
}
